'use client';

import { CheckIcon, MaximizeIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';

import { Chapter } from '@/features/fanfic-page/types/fanfic';
import { useAgeConfirmationStore } from '@/features/age-verification/store';
import { hasAgeConfirmation } from '@/features/age-verification/utils';
import { useProfile } from '@/hooks/use-profile';
import { CalendarIcon } from '../../icons/calendar';
import { SpeakIcon } from '../../icons/speak';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { useModalStore } from '@/store/modal.store';
import { formatWordCount } from './chapter-item.utils';

interface ChapterItemProps {
  chapter: Chapter;
  isSelected: boolean;
  isRating18: boolean;
  workId: string;
  workPathPrefix: '/fanfics' | '/originals';
  isRead?: boolean;
  onMarkAsRead?: () => void;
  progress?: number;
  wordCount?: number;
  onOpenFullscreen?: (chapterId: string) => void;
}

export const ChapterItem = React.memo(
  ({
    chapter,
    isRead = false,
    onMarkAsRead,
    progress = 0,
    wordCount = 0,
    onOpenFullscreen,
    isRating18,
    workId,
    workPathPrefix,
  }: ChapterItemProps) => {
    const session = useSession();
    const { userData } = useProfile();
    const [showTooltip, setShowTooltip] = useState(false);
    const isMobile = useIsMobile();
    const { setIsOpenAuthDialog } = useModalStore();
    const { checkAndExecute, hasLocalConfirmation } = useAgeConfirmationStore();

    const isAuthenticated = !!session?.data?.user;
    const hasConfirmedAge = hasAgeConfirmation(userData) || hasLocalConfirmation;
    const chapterUrl = `${workPathPrefix}/${workId}/read/${chapter.id}`;

    // Determine if link should be followed by search engines
    const shouldIndexing = !isRating18 && chapter.shouldIndexing;
    const relAttribute = shouldIndexing ? undefined : 'nofollow';

    const handleMarkAsRead = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!session?.data?.user) {
        setIsOpenAuthDialog(true);
        return;
      }
      if (onMarkAsRead) {
        onMarkAsRead();
      }
    };

    const handleOpenFullscreen = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      checkAndExecute(
        () => {
          if (onOpenFullscreen) {
            onOpenFullscreen(chapter.id);
          }
        },
        isRating18,
        isAuthenticated,
        hasConfirmedAge,
      );
    };

    const handleChapterClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!isRating18 || hasConfirmedAge) {
        return;
      }

      e.preventDefault();
      checkAndExecute(
        () => {
          window.location.href = chapterUrl;
        },
        isRating18,
        isAuthenticated,
        hasConfirmedAge,
      );
    };

    return (
      <div className="relative rounded-2xl p-4 border border-[#E5E7EB] hover:border-[#D1D5DB] transition-colors">
        <Link
          href={chapterUrl}
          onClick={handleChapterClick}
          className="w-full h-full text-left flex flex-col justify-center"
          rel={relAttribute}
        >
          <div className="grid grid-cols-[1fr_max-content] gap-2 items-start">
            <div className="text-base font-semibold text-[#111827]">{chapter.title}</div>
            <div className="flex items-center gap-2">
              {onOpenFullscreen && (
                <button
                  type="button"
                  onClick={handleOpenFullscreen}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Открыть в полноэкранном режиме"
                >
                  <MaximizeIcon className="w-4 h-4" />
                </button>
              )}
              {onMarkAsRead && (
                <TooltipProvider delayDuration={0} skipDelayDuration={0}>
                  <Tooltip open={showTooltip} onOpenChange={setShowTooltip}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={handleMarkAsRead}
                        className={`w-4 h-4 rounded-full border transition-colors flex items-center justify-center ${
                          isRead
                            ? 'bg-[#505CFF] border-[#505CFF]'
                            : 'border-[#E3E5ED] hover:border-[#C4C9D9]'
                        }`}
                        onMouseEnter={() => isMobile && setShowTooltip(true)}
                        onKeyDown={(e) => {
                          e.preventDefault();
                          if (e.key === 'Enter') {
                            setShowTooltip(!showTooltip);
                          }
                        }}
                      >
                        {isRead && <CheckIcon className="w-3 h-3 text-white mt-0.5" />}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Отметить как прочитанное</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-4 text-xs text-[#6e7384]">
            {chapter.publishedAt && (
              <div className="flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" />
                <span>{new Date(chapter.publishedAt).toLocaleDateString('ru-RU')}</span>
              </div>
            )}

            {wordCount > 0 && (
              <div className="flex items-center gap-1">
                <SpeakIcon className="w-3 h-3" />
                <span>{formatWordCount(wordCount)}</span>
              </div>
            )}

            <span className="flex items-center gap-1">
              <span>{(progress || 0)?.toFixed()}%</span>
              <div className="flex-1 bg-gray-200 rounded-full h-1.5 w-20">
                <div
                  className="bg-[#505CFF] h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </span>
          </div>
        </Link>
      </div>
    );
  },
);
